import React from "react";
import { Container, Row } from "react-bootstrap";
import { useLoaderData } from "react-router";
import { Link } from "react-router-dom";

import classNames from "classnames";

import { store } from "../..";
import "../../assets/css/wibb/pages/playoffs.scss";
import { globalActions, selectFields } from "../../features/global/globalSlice";
import {
    selectSelectedTournament,
    tournamentManagementActions,
} from "../../features/tourneymgmt/tournamentManagementSlice";
import { useAppSelector } from "../../hooks/reduxHooks";
import { getBracket } from "../../lib/api";
import { TEAM_LOGO_URL } from "../../lib/globals";
import { PrintIcon } from "../../lib/icons";
import { isChildTournament } from "../../lib/utils";
import { IPlayoff } from "../../types/api";

export const loader = async ({ params }: any) => {
    store.dispatch(
        tournamentManagementActions.setselectedTournamentId(
            parseInt(params.tournamentId),
        ),
    );

    let state = store.getState();
    const promises = [];
    let tournaments = state.tournamentmanagement.tournaments;
    let masterTournaments = state.tournamentmanagement.masterTournaments;
    const fields = state.global.fields;

    !tournaments.length &&
        promises.push(
            store.dispatch(tournamentManagementActions.getTournaments()),
        );

    !masterTournaments.length &&
        promises.push(
            store.dispatch(tournamentManagementActions.getMasterTournaments()),
        );

    !fields.length && promises.push(store.dispatch(globalActions.getFields()));

    await Promise.all(promises);

    state = store.getState();

    masterTournaments = state.tournamentmanagement.masterTournaments;
    tournaments = state.tournamentmanagement.tournaments;

    const tournamentId = state.tournamentmanagement.selectedTournament;

    const tournament = selectSelectedTournament(state);

    const isChild = isChildTournament(tournament);
    const masterTournament = isChild
        ? masterTournaments.find(
              mt =>
                  mt.id ===
                  tournaments.find(t => t.id === tournamentId)?.parent,
          )
        : tournament;

    const header = isChild
        ? `${masterTournament?.name} - ${tournament.ageLevel} Bracket`
        : `${tournament?.name} - All Brackets`;

    const [{ bracket }] = await Promise.all([getBracket(params.tournamentId)]);

    return {
        tournament,
        bracket,
        header,
    };
};

const Bracket = (): React.JSX.Element => {
    const { bracket: loadedBracket } = useLoaderData() as {
        bracket: IPlayoff | IPlayoff[];
    };

    const { tournaments } = useAppSelector(state => state.tournamentmanagement);
    const fields = useAppSelector(selectFields);

    const buildBracket = (
        bracketType: "championship" | "consolation",
        bracket: IPlayoff,
    ) => {
        let bracketOutput = [];
        const tournament = tournaments.find(t => t.id === bracket.tournament);
        for (let i = 1; i <= bracket.rounds[bracketType]!; i += 1) {
            bracketOutput.push(
                <div className={`round round-${i}`} key={`round-${i}`}>
                    {bracket.games[bracketType]!.filter(g => g.round === i).map(
                        (g, idx, games) => {
                            let date =
                                g.date !== null
                                    ? new Intl.DateTimeFormat("en-US", {
                                          dateStyle: "long",
                                          timeStyle: "short",
                                      }).format(new Date(g.date))
                                    : "TBD";

                            const isChampionshipGame =
                                bracketType === "championship" &&
                                i === bracket.rounds[bracketType] &&
                                idx === games.length - 1;

                            const tournamentChampion =
                                isChampionshipGame &&
                                g.teams.home.score !== null &&
                                g.teams.visitor.score !== null
                                    ? g.teams.home.score > g.teams.visitor.score
                                        ? g.teams.home
                                        : g.teams.visitor
                                    : null;

                            return (
                                <div
                                    className="match"
                                    id={`${bracketType}-game-${g.playoffGameId}`}
                                    key={`${bracketType}-match-${g.playoffGameId}`}
                                >
                                    {g.teams.visitor.seed ||
                                    g.teams.visitor.team ? (
                                        <div className="team">
                                            <span className="seed">
                                                #
                                                {g.teams.visitor.seed ||
                                                    (g.teams.visitor.id &&
                                                        tournament?.standings?.[
                                                            g.teams.visitor.id
                                                        ].standing)}
                                            </span>
                                            {g.teams.visitor.logo !== null && (
                                                <span className="teamLogo">
                                                    <img
                                                        src={`${TEAM_LOGO_URL}${g.teams.visitor.logo}`}
                                                        alt={`${g.teams.visitor.team} logo`}
                                                    />
                                                </span>
                                            )}
                                            <span className="teamName">
                                                {g.teams.visitor.team}
                                            </span>
                                            {g.teams.visitor.score !== null && (
                                                <span className="score">
                                                    {g.teams.visitor.score}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="team winnerOf">
                                            Winner of{" "}
                                            {bracketType === "consolation" &&
                                                "Consolation"}{" "}
                                            Game {g.teams.visitor.winnerOf}
                                        </div>
                                    )}

                                    <div className="gameInfo">
                                        <span className="gameId">
                                            {isChampionshipGame
                                                ? "Championship"
                                                : bracketType ===
                                                      "consolation" &&
                                                  "Consolation"}{" "}
                                            Game{" "}
                                            {!isChampionshipGame &&
                                                g.playoffGameId}
                                        </span>
                                        {tournamentChampion !== null && (
                                            <div className="tournamentChampion">
                                                <h5>Champion</h5>
                                                {tournamentChampion.logo !==
                                                    null && (
                                                    <div className="teamLogo">
                                                        <img
                                                            src={`${TEAM_LOGO_URL}${tournamentChampion.logo}`}
                                                            alt={`${tournamentChampion.team} logo`}
                                                        />
                                                    </div>
                                                )}
                                                <span className="teamName">
                                                    {tournamentChampion.id &&
                                                        `#${tournament?.standings?.[tournamentChampion.id].standing}`}{" "}
                                                    {tournamentChampion.team}
                                                </span>
                                            </div>
                                        )}
                                        <span className="date">{date}</span>
                                        <span className="field">
                                            <Link
                                                to={`/fields/${g.field}`}
                                                target="_blank"
                                            >
                                                <span
                                                    className={classNames(
                                                        "noPrint",
                                                    )}
                                                >
                                                    {g.fieldName}
                                                    <br />
                                                </span>
                                                {g.diamond && (
                                                    <>
                                                        Diamond:{" "}
                                                        {
                                                            fields
                                                                .find(
                                                                    f =>
                                                                        f.id ===
                                                                        g.field,
                                                                )
                                                                ?.diamonds.find(
                                                                    d =>
                                                                        d.id ===
                                                                        g.diamond,
                                                                )?.name
                                                        }
                                                    </>
                                                )}
                                            </Link>
                                        </span>
                                    </div>

                                    {g.teams.home.seed || g.teams.home.team ? (
                                        <div className="team">
                                            <span className="seed">
                                                #
                                                {g.teams.home.seed ||
                                                    (g.teams.home.id &&
                                                        tournament?.standings?.[
                                                            g.teams.home.id
                                                        ].standing)}
                                            </span>
                                            {g.teams.home.logo !== null && (
                                                <span className="teamLogo">
                                                    <img
                                                        src={`${TEAM_LOGO_URL}${g.teams.home.logo}`}
                                                        alt={`${g.teams.home.team} logo`}
                                                    />
                                                </span>
                                            )}
                                            <span className="teamName">
                                                {g.teams.home.team}
                                            </span>
                                            {g.teams.home.score !== null && (
                                                <span className="score">
                                                    {g.teams.home.score}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="team winnerOf">
                                            Winner of{" "}
                                            {bracketType === "consolation" &&
                                                "Consolation"}{" "}
                                            Game {g.teams.home.winnerOf}
                                        </div>
                                    )}
                                </div>
                            );
                        },
                    )}
                </div>,
            );
        }

        return bracketOutput;
    };

    return (
        <div
            data-testid="playoffBracketContent"
            className={classNames("playoffBracketContent")}
        >
            <div className={classNames("d-flex", "mb-2", "noPrint")}>
                <PrintIcon
                    className="printerIcon d-none d-lg-inline-block"
                    size="2x"
                    title="Print bracket"
                    onClick={() => window.print()}
                />
            </div>
            {!Array.isArray(loadedBracket) ? (
                <Container>
                    <Row
                        className="bracket"
                        data-bracketid={loadedBracket.bracketId}
                    >
                        <h3 className="championship header">
                            Championship Games
                        </h3>
                        {buildBracket("championship", loadedBracket)}
                        {loadedBracket.games.consolation && (
                            <div className={classNames("printBreakBefore")}>
                                <h3 className="consolation header">
                                    Consolation Games
                                </h3>
                                {buildBracket("consolation", loadedBracket)}
                            </div>
                        )}
                    </Row>
                </Container>
            ) : (
                <Container className="multiDisplay">
                    {loadedBracket
                        .sort((a, b) => {
                            const aAge = tournaments.find(
                                t => t.id === a.tournament,
                            )?.ageLevel;
                            const bAge = tournaments.find(
                                t => t.id === b.tournament,
                            )?.ageLevel;

                            if (aAge && !bAge) {
                                return 1;
                            } else if (bAge && !aAge) {
                                return -1;
                            } else if (aAge && bAge) {
                                return aAge?.localeCompare(bAge);
                            } else {
                                return 0;
                            }
                        })
                        .map(b => (
                            <Row
                                className="bracket"
                                data-bracketid={b.bracketId}
                                key={b.tournament}
                            >
                                <h3 className="bracketAge">
                                    {
                                        tournaments.find(
                                            t => t.id === b.tournament,
                                        )?.ageLevel
                                    }
                                </h3>
                                <h3 className="championship header">
                                    Championship Games
                                </h3>
                                {buildBracket("championship", b)}
                                {b.games.consolation && (
                                    <>
                                        <h3 className="consolation header">
                                            Consolation Games
                                        </h3>
                                        {buildBracket("consolation", b)}
                                    </>
                                )}
                            </Row>
                        ))}
                </Container>
            )}
        </div>
    );
};

export default Bracket;
